import { createSlice } from '@reduxjs/toolkit'

/**
 * Resolved linked-order groups, keyed by TMS_LINK_REFS ref id:
 *   { byRef: { [refId]: { lastDeliveryId, firstPickupId, stops: [{ orderType, orderId }] } } }
 *
 * Plus the group members' order fields, keyed by order id:
 *   { fieldsById: { [orderId]: { ...order fields, id, isArchived? } } }
 *
 * `fieldsById` is what makes archived legs visible to the rest of the app. A closed
 * order leaves Firestore once expiredAt passes and lives on in ARCHIVE.Orders, and
 * because expiredAt is per order, one group's legs expire on different days. The
 * OrderTable fetch resolves every member once — grid rows first, then Firestore /
 * BigQuery for whatever is missing — and publishes the result here, so the invoice,
 * BOL and order-detail paths read a resolved sibling instead of each re-fetching it.
 *
 * Kept in its OWN slice — NOT in `data` — because the OrderTable fetch pipeline
 * (data → preRequisites → fetch) re-runs whenever `data` changes. Storing this
 * here ensures dispatching it cannot re-trigger the fetch (which caused an
 * infinite reload loop). Populated by OrderTable and the dispatch dashboard on
 * order load; read by the grid / OrderDisplay / ExportFiles / dispatch highlight.
 */
const linkedGroupsSlice = createSlice({
  name: 'linkedGroups',
  initialState: { byRef: {} as Record<string, any>, fieldsById: {} as Record<string, any> },
  reducers: {
    setLinkedGroups: (state, action) => {
      state.byRef = { ...(state.byRef || {}), ...(action.payload || {}) }
    },
    setLinkedOrderFields: (state, action) => {
      state.fieldsById = { ...(state.fieldsById || {}), ...(action.payload || {}) }
    },
  },
})

export const { setLinkedGroups, setLinkedOrderFields } = linkedGroupsSlice.actions
export default linkedGroupsSlice.reducer
